import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CreditCard,
  History,
  LifeBuoy,
  MapPin,
  MessageSquare,
  Package,
  ShieldBan,
  ShieldCheck,
  Smartphone,
  CheckCircle2,
  ExternalLink,
} from 'lucide-react';
import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';

import { Badge, type BadgeTone, Card, PageHeader, Spinner } from '@terra/ui';

import { api } from '../lib/api';

const formatPaise = (p: number) =>
  `₹${(p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const formatDate = (iso: string) =>
  new Intl.DateTimeFormat('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));

export function CustomerDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['customer', '360', id],
    queryFn: () => api.customers.get360(id!),
    enabled: !!id,
  });

  const [activeTab, setActiveTab] = useState<
    'orders' | 'wallet' | 'support' | 'risk' | 'addresses' | 'notes'
  >('orders');

  if (isLoading) {
    return (
      <div className="flex h-[400px] flex-col items-center justify-center gap-4">
        <Spinner size="lg" />
        <p className="text-ink-muted text-[13px] font-medium">Loading customer 360 profile...</p>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex h-[400px] flex-col items-center justify-center gap-4">
        <AlertTriangle className="h-10 w-10 text-red-500 opacity-50" />
        <p className="text-ink-muted text-[13px] font-medium">
          Customer not found or failed to load.
        </p>
        <Link
          to="/customers"
          className="text-gold-600 hover:text-gold-700 text-[13px] font-bold underline underline-offset-2"
        >
          Back to list
        </Link>
      </div>
    );
  }

  const { customer, orders, wallet, support, risk, addresses, notes } = data;

  const headerActions = (
    <div className="flex gap-2">
      <Link
        to="/customers"
        className="bg-surface border-border/60 hover:bg-surface-subtle flex h-9 items-center rounded-xl border px-4 text-[13px] font-bold shadow-sm transition-all"
      >
        Back to List
      </Link>
    </div>
  );

  return (
    <div className="space-y-6 pb-12">
      <PageHeader title={customer.name} description={customer.email} actions={headerActions} />

      {/* Hero 360 Stats Overview */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="flex items-center gap-4 p-5">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-blue-50 text-blue-600">
            <Package className="h-5 w-5" />
          </div>
          <div>
            <p className="text-ink-muted text-[12px] font-bold uppercase tracking-wider">Orders</p>
            <p className="text-ink mt-0.5 text-2xl font-black">{orders.length}</p>
          </div>
        </Card>
        <Card className="flex items-center gap-4 p-5">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-emerald-50 text-emerald-600">
            <CreditCard className="h-5 w-5" />
          </div>
          <div>
            <p className="text-ink-muted text-[12px] font-bold uppercase tracking-wider">
              Wallet Balance
            </p>
            <p className="text-ink mt-0.5 text-2xl font-black">
              {formatPaise(customer.walletBalancePaise)}
            </p>
          </div>
        </Card>
        <Card className="flex items-center gap-4 p-5">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-orange-50 text-orange-600">
            <LifeBuoy className="h-5 w-5" />
          </div>
          <div>
            <p className="text-ink-muted text-[12px] font-bold uppercase tracking-wider">
              Support Tickets
            </p>
            <p className="text-ink mt-0.5 text-2xl font-black">{support.length}</p>
          </div>
        </Card>
        <Card className="flex items-center gap-4 p-5">
          <div
            className={`grid h-12 w-12 shrink-0 place-items-center rounded-2xl ${risk.churnRiskBps > 5000 ? 'bg-red-50 text-red-600' : 'bg-purple-50 text-purple-600'}`}
          >
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div>
            <p className="text-ink-muted text-[12px] font-bold uppercase tracking-wider">
              Churn Risk
            </p>
            <p className="text-ink mt-0.5 text-2xl font-black">
              {(risk.churnRiskBps / 100).toFixed(1)}%
            </p>
          </div>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column: Profile Card */}
        <div className="space-y-6 lg:col-span-1">
          <Card>
            <div className="border-border/60 flex items-center gap-4 border-b p-5">
              <div className="bg-gold-500 grid h-14 w-14 place-items-center rounded-full text-xl font-bold text-white shadow-sm">
                {customer.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <h2 className="text-ink text-lg font-black">{customer.name}</h2>
                <div className="mt-1 flex items-center gap-2">
                  <Badge
                    tone={customer.blocked ? 'danger' : customer.verified ? 'success' : 'neutral'}
                    className="px-2 py-0.5 text-[10px]"
                  >
                    {customer.blocked ? 'Blocked' : customer.verified ? 'Verified' : 'Unverified'}
                  </Badge>
                  {customer.isSuspicious && (
                    <Badge tone="warning" className="px-2 py-0.5 text-[10px]">
                      Suspicious
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-4 p-5">
              <div>
                <p className="text-ink-muted text-[11px] font-bold uppercase tracking-wider">
                  Contact Info
                </p>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="text-ink-muted h-4 w-4" />
                    <span className="text-ink text-[13px] font-medium">{customer.email}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Smartphone className="text-ink-muted h-4 w-4" />
                    <span className="text-ink text-[13px] font-medium">{customer.phone}</span>
                  </div>
                </div>
              </div>
              <div className="border-border/60 border-t pt-4">
                <p className="text-ink-muted mb-2 text-[11px] font-bold uppercase tracking-wider">
                  Consents & Settings
                </p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-ink text-[13px] font-medium">Marketing Consent</span>
                    {customer.marketingConsent ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <ShieldBan className="text-ink-subtle h-4 w-4" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-ink text-[13px] font-medium">WhatsApp Opt-in</span>
                    {customer.whatsappConsent ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <ShieldBan className="text-ink-subtle h-4 w-4" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-ink text-[13px] font-medium">Classifieds Package</span>
                    <span className="text-ink text-[13px] font-bold">
                      {customer.packageName ?? 'None'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="border-border/60 border-t pt-4">
                <p className="text-ink-muted text-[11px] font-medium">
                  Customer since {formatDate(customer.createdAt)}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Right Column: Deep Details */}
        <div className="lg:col-span-2">
          <Card>
            <div className="border-border/60 border-b">
              <div className="flex gap-1 p-2">
                {[
                  { id: 'orders', label: 'Orders' },
                  { id: 'wallet', label: 'Wallet' },
                  { id: 'support', label: 'Support' },
                  { id: 'addresses', label: 'Addresses' },
                  { id: 'risk', label: 'Risk & Fraud' },
                  { id: 'notes', label: 'Notes' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`rounded-lg px-4 py-2 text-[13px] font-bold transition-all ${
                      activeTab === tab.id
                        ? 'bg-ink text-white shadow-sm'
                        : 'text-ink-muted hover:bg-surface-subtle hover:text-ink'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-0">
              {activeTab === 'orders' &&
                (orders.length === 0 ? (
                  <div className="text-ink-muted p-8 text-center text-[13px] font-medium">
                    No orders found.
                  </div>
                ) : (
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[11px] font-bold uppercase tracking-wider">
                        <th className="px-5 py-3">Order ID</th>
                        <th className="px-5 py-3">Date</th>
                        <th className="px-5 py-3">Total</th>
                        <th className="px-5 py-3">Status</th>
                        <th className="px-5 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((o: any) => (
                        <tr
                          key={o.id}
                          className="border-border/60 hover:bg-surface-subtle/30 border-b transition-colors"
                        >
                          <td className="text-ink px-5 py-4 text-[13px] font-bold">
                            {o.orderNumber}
                          </td>
                          <td className="text-ink-muted px-5 py-4 text-[13px] font-medium">
                            {formatDate(o.createdAt)}
                          </td>
                          <td className="text-ink px-5 py-4 text-[13px] font-bold">
                            {formatPaise(o.grandTotalPaise)}
                          </td>
                          <td className="px-5 py-4">
                            <Badge
                              tone={
                                o.status === 'DELIVERED'
                                  ? 'success'
                                  : o.status === 'CANCELLED'
                                    ? 'danger'
                                    : 'neutral'
                              }
                              className="px-1.5 py-0.5 text-[10px]"
                            >
                              {o.status}
                            </Badge>
                          </td>
                          <td className="px-5 py-4 text-right">
                            <Link
                              to={`/orders/${o.id}`}
                              className="text-gold-600 hover:text-gold-700 flex items-center justify-end gap-1 text-[12px] font-bold"
                            >
                              View <ExternalLink className="h-3 w-3" />
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ))}

              {activeTab === 'wallet' &&
                (wallet.length === 0 ? (
                  <div className="text-ink-muted p-8 text-center text-[13px] font-medium">
                    No wallet credits found.
                  </div>
                ) : (
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[11px] font-bold uppercase tracking-wider">
                        <th className="px-5 py-3">Credit ID</th>
                        <th className="px-5 py-3">Bucket</th>
                        <th className="px-5 py-3">Remaining</th>
                        <th className="px-5 py-3">Status</th>
                        <th className="px-5 py-3">Expires</th>
                      </tr>
                    </thead>
                    <tbody>
                      {wallet.map((w: any) => (
                        <tr
                          key={w.id}
                          className="border-border/60 hover:bg-surface-subtle/30 border-b transition-colors"
                        >
                          <td className="text-ink-muted px-5 py-4 font-mono text-[12px]">
                            {w.id.slice(-8)}
                          </td>
                          <td className="text-ink px-5 py-4 text-[13px] font-bold">{w.bucket}</td>
                          <td className="text-ink px-5 py-4 text-[13px] font-black">
                            {formatPaise(w.remainingPaise)}
                          </td>
                          <td className="px-5 py-4">
                            <Badge
                              tone={
                                w.status === 'ACTIVE'
                                  ? 'success'
                                  : w.status === 'EXPIRED'
                                    ? 'danger'
                                    : 'neutral'
                              }
                              className="px-1.5 py-0.5 text-[10px]"
                            >
                              {w.status}
                            </Badge>
                          </td>
                          <td className="text-ink-muted px-5 py-4 text-[13px] font-medium">
                            {w.expiresAt ? formatDate(w.expiresAt) : 'Never'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ))}

              {activeTab === 'support' &&
                (support.length === 0 ? (
                  <div className="text-ink-muted p-8 text-center text-[13px] font-medium">
                    No support tickets raised.
                  </div>
                ) : (
                  <div className="divide-border/60 divide-y">
                    {support.map((t: any) => (
                      <div key={t.id} className="hover:bg-surface-subtle/30 p-5 transition-colors">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-ink text-[14px] font-bold">
                                {t.ticketNumber}
                              </span>
                              <Badge
                                tone={
                                  t.status === 'OPEN' || t.status === 'IN_PROGRESS'
                                    ? 'warning'
                                    : 'neutral'
                                }
                                className="px-1.5 py-0.5 text-[10px]"
                              >
                                {t.status}
                              </Badge>
                            </div>
                            <p className="text-ink mt-1 text-[13px] font-medium">
                              {t.subject || 'No subject provided'}
                            </p>
                            <div className="text-ink-muted mt-2 flex items-center gap-4 text-[12px] font-medium">
                              <span>{t.category}</span>
                              <span className="bg-border/60 h-1 w-1 rounded-full" />
                              <span>{formatDate(t.createdAt)}</span>
                            </div>
                          </div>
                          <Badge
                            tone={
                              t.priority === 'URGENT' || t.priority === 'HIGH'
                                ? 'danger'
                                : 'neutral'
                            }
                            className="px-2 py-1 text-[10px]"
                          >
                            {t.priority}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}

              {activeTab === 'addresses' &&
                (addresses.length === 0 ? (
                  <div className="text-ink-muted p-8 text-center text-[13px] font-medium">
                    No saved addresses.
                  </div>
                ) : (
                  <div className="grid gap-4 p-5 sm:grid-cols-2">
                    {addresses.map((a: any) => (
                      <div
                        key={a.id}
                        className="border-border/60 relative rounded-xl border p-4 shadow-sm"
                      >
                        {a.isDefault && (
                          <div className="absolute right-4 top-4">
                            <Badge tone="success" className="px-1.5 py-0.5 text-[9px]">
                              DEFAULT
                            </Badge>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <MapPin className="text-ink-muted h-4 w-4" />
                          <span className="text-ink text-[13px] font-bold">
                            {a.label || 'Address'}
                          </span>
                        </div>
                        <div className="text-ink mt-3 text-[13px] font-medium leading-relaxed">
                          <p>{a.contactName}</p>
                          <p className="text-ink-muted">{a.contactPhone}</p>
                          <p className="mt-2">
                            {a.city}, {a.state} {a.pincode}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}

              {activeTab === 'risk' &&
                (risk.signals.length === 0 ? (
                  <div className="flex flex-col items-center justify-center p-12 text-center">
                    <ShieldCheck className="h-12 w-12 text-emerald-500 opacity-50" />
                    <p className="text-ink mt-4 text-[14px] font-bold">No Risk Signals</p>
                    <p className="text-ink-muted mt-1 text-[13px] font-medium">
                      This customer profile appears healthy.
                    </p>
                  </div>
                ) : (
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[11px] font-bold uppercase tracking-wider">
                        <th className="px-5 py-3">Signal</th>
                        <th className="px-5 py-3">Severity</th>
                        <th className="px-5 py-3">Score</th>
                        <th className="px-5 py-3">Status</th>
                        <th className="px-5 py-3">Detected On</th>
                      </tr>
                    </thead>
                    <tbody>
                      {risk.signals.map((s: any) => (
                        <tr
                          key={s.id}
                          className="border-border/60 hover:bg-surface-subtle/30 border-b transition-colors"
                        >
                          <td className="text-ink px-5 py-4 text-[13px] font-bold">{s.kind}</td>
                          <td className="px-5 py-4">
                            <Badge
                              tone={
                                s.severity === 'HIGH' || s.severity === 'CRITICAL'
                                  ? 'danger'
                                  : 'warning'
                              }
                              className="px-1.5 py-0.5 text-[10px]"
                            >
                              {s.severity}
                            </Badge>
                          </td>
                          <td className="text-ink px-5 py-4 text-[13px] font-black">
                            {s.score}/100
                          </td>
                          <td className="px-5 py-4">
                            <Badge
                              tone={s.status === 'OPEN' ? 'warning' : 'neutral'}
                              className="px-1.5 py-0.5 text-[10px]"
                            >
                              {s.status}
                            </Badge>
                          </td>
                          <td className="text-ink-muted px-5 py-4 text-[13px] font-medium">
                            {formatDate(s.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ))}

              {activeTab === 'notes' &&
                (notes.length === 0 ? (
                  <div className="text-ink-muted p-8 text-center text-[13px] font-medium">
                    No internal notes for this customer.
                  </div>
                ) : (
                  <div className="divide-border/60 divide-y">
                    {notes.map((n: any) => (
                      <div key={n.id} className="hover:bg-surface-subtle/30 p-5 transition-colors">
                        <div className="flex items-center gap-2">
                          <Badge tone="neutral" className="px-1.5 py-0.5 text-[10px]">
                            {n.type}
                          </Badge>
                          <span className="text-ink-muted text-[11px] font-medium">
                            {formatDate(n.createdAt)}
                          </span>
                        </div>
                        <p className="text-ink mt-2 text-[13px] font-medium leading-relaxed">
                          {n.description}
                        </p>
                      </div>
                    ))}
                  </div>
                ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
